import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, ListChecks } from 'lucide-react';
import { Chargement, MessageErreur, Pastille, PastilleSite, type TonPastille } from '../components/ui/Primitives';
import { BarreAvancement, tauxLisible } from '../components/lecteur/Progression';
import { jourLisible } from '../components/ecf/format';
import { ErreurApi } from '../lib/api';
import { momentRelatif } from '../lib/moment-relatif';
import { echeanceLongue, LIBELLES_STATUT_DEVOIR, noteSur20 } from '../lib/affichage-devoirs';
import { pourcentagePresence, type CoursEnProgression, type QuizEnProgression } from '../lib/useEspaceApprenant';
import {
  useFicheProgression,
  type DevoirSuivi,
  type FicheProgressionApprenant,
  type SignalRetard,
} from '../lib/useEspaceFormateur';

/**
 * Fiche de progression d'un apprenant (TF-458), ouverte depuis « Mes
 * promotions » : ce que l'apprenant lit dans « Mes formations » — bloc par
 * bloc, ses cours en ligne module par module et ses quiz —, ses devoirs avec
 * leur statut, leur retard et leur note, son assiduité, et ce qui le met en
 * retard. Lecture seule : tout vient de l'API, rien n'est recompté ni ouvert
 * au nom de l'apprenant. Un apprenant hors des promotions du lecteur est
 * introuvable.
 */

function pluriel(nombre: number, singulier: string, plurielForme = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielForme : singulier}`;
}

function nombre(valeur: number): string {
  return String(valeur).replace('.', ',');
}

function Tuile({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-5">
      <div className="text-xs uppercase tracking-wide text-codialis-gray">{libelle}</div>
      {children}
    </div>
  );
}

function Valeur({ children, alerte = false }: { children: ReactNode; alerte?: boolean }): JSX.Element {
  return (
    <div className={`font-display mt-1 text-3xl font-extrabold ${alerte ? 'text-red-600' : 'text-codialis-navy'}`}>
      {children}
    </div>
  );
}

/** Pourquoi le signal est levé, avec les chiffres de l'API. */
function motifDuSignal(signal: SignalRetard, fiche: FicheProgressionApprenant): string {
  const { apprenant, promotion, regles } = fiche;
  if (signal === 'DEVOIRS') {
    return `${pluriel(apprenant.devoirs.enRetard, 'devoir ou quiz', 'devoirs ou quiz')} à rendre dont l’échéance est passée.`;
  }
  if (signal === 'ABSENTEISME') {
    return `${pourcentagePresence(apprenant.absenteisme.taux)} d’absentéisme sur ses feuilles clôturées, au-delà du seuil de ${regles.seuilAbsenteisme} %.`;
  }
  const ecart = (promotion.tauxMoyen ?? 0) - (apprenant.avancement.taux ?? 0);
  return `${tauxLisible(apprenant.avancement)} de progression, ${ecart} points sous la moyenne de sa promotion (${
    promotion.tauxMoyen ?? '—'
  } %).`;
}

function CoursSuivi({ cours }: { cours: CoursEnProgression }): JSX.Element {
  return (
    <li className="rounded-xl border border-gray-100 p-3 sm:p-4">
      <div className="font-semibold text-codialis-navy">{cours.titre}</div>
      <div className="text-xs text-codialis-gray">Par {cours.auteur}</div>
      <div className="mt-2 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <BarreAvancement avancement={cours.avancement} libelle={`Progression du cours ${cours.titre}`} />
        </div>
        <span className="w-10 shrink-0 text-right text-xs font-semibold text-codialis-navy">
          {tauxLisible(cours.avancement)}
        </span>
      </div>
      <ol className="mt-2 divide-y divide-gray-50">
        {cours.modules.map((module, rang) => (
          <li key={module.id} className="flex items-center gap-2 py-1.5 text-sm">
            {module.avancement.taux === 100 ? (
              <>
                <Check className="h-4 w-4 shrink-0 text-codialis-mint" aria-hidden />
                <span className="sr-only">Complété :</span>
              </>
            ) : (
              <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300" aria-hidden />
            )}
            <span className="min-w-0 flex-1 truncate text-codialis-navy">
              Module {rang + 1} · {module.titre}
            </span>
            <span className="shrink-0 text-xs text-codialis-gray">
              {module.avancement.taux === null
                ? 'à venir'
                : `${module.avancement.faits}/${module.avancement.total} · ${tauxLisible(module.avancement)}`}
            </span>
          </li>
        ))}
      </ol>
    </li>
  );
}

function QuizSuivi({ quiz }: { quiz: QuizEnProgression }): JSX.Element {
  return (
    <li className="flex min-h-[44px] flex-wrap items-center gap-x-3 gap-y-1 px-2 py-2">
      <ListChecks className={`h-4 w-4 shrink-0 ${quiz.fait ? 'text-codialis-mint' : 'text-codialis-sky'}`} aria-hidden />
      <span className="min-w-0 flex-1 basis-40 text-sm text-codialis-navy">
        {quiz.titre}
        {quiz.cours && <span className="block text-[11px] text-codialis-gray">{quiz.cours}</span>}
      </span>
      {quiz.fait ? (
        <Pastille texte={quiz.note === null ? 'Fait' : `Fait · ${noteSur20(quiz.note)}`} ton="success" />
      ) : (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-codialis-gray">avant le {echeanceLongue(quiz.echeance)}</span>
          <Pastille texte="À faire" ton="warning" />
        </span>
      )}
    </li>
  );
}

function BlocSuivi({
  numero,
  libelle,
  bloc,
}: {
  numero: number;
  libelle: string;
  bloc: FicheProgressionApprenant['progression']['blocs'][number] | undefined;
}): JSX.Element {
  const avancement = bloc?.avancement ?? null;
  const fini = avancement !== null && avancement.taux === 100;
  const enLigne = (bloc?.cours.length ?? 0) + (bloc?.quiz.length ?? 0);
  return (
    <details open={numero === 1} className="group border-b border-gray-100 last:border-b-0">
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 transition hover:bg-gray-50 sm:px-5 [&::-webkit-details-marker]:hidden">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white ${
            fini ? 'bg-codialis-mint' : avancement && avancement.faits > 0 ? 'bg-codialis-sky' : 'bg-gray-300'
          }`}
        >
          {fini ? (
            <>
              <Check className="h-4 w-4" aria-hidden />
              <span className="sr-only">Complété</span>
            </>
          ) : (
            numero
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-codialis-gray">Bloc {numero}</span>
          <span className="block text-sm font-semibold text-codialis-navy">{libelle}</span>
          <span className="block text-xs text-codialis-gray">
            {enLigne === 0
              ? 'Rien en ligne'
              : [
                  bloc && bloc.cours.length > 0 ? pluriel(bloc.cours.length, 'cours', 'cours') : null,
                  bloc && bloc.quiz.length > 0 ? pluriel(bloc.quiz.length, 'quiz', 'quiz') : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </span>
        </span>
        {avancement && avancement.taux !== null && (
          <span className={`shrink-0 text-xs font-bold ${fini ? 'text-codialis-mint-clair' : 'text-codialis-sky'}`}>
            {tauxLisible(avancement)}
          </span>
        )}
        <ChevronDown className="h-4 w-4 shrink-0 text-codialis-gray transition group-open:rotate-180" aria-hidden />
      </summary>
      <div className="space-y-3 px-4 pb-4 sm:px-5 md:pl-[4.25rem]">
        {avancement && avancement.taux !== null && (
          <BarreAvancement avancement={avancement} libelle={`Progression du bloc ${numero}`} />
        )}
        {enLigne === 0 ? (
          <p className="text-xs text-codialis-gray">Aucun cours en ligne ni quiz pour ce bloc.</p>
        ) : (
          <>
            {bloc!.cours.length > 0 && (
              <ul className="space-y-2" aria-label={`Cours en ligne du bloc ${numero}`}>
                {bloc!.cours.map((cours) => (
                  <CoursSuivi key={cours.id} cours={cours} />
                ))}
              </ul>
            )}
            {bloc!.quiz.length > 0 && (
              <ul className="divide-y divide-gray-50" aria-label={`Quiz du bloc ${numero}`}>
                {bloc!.quiz.map((quiz) => (
                  <QuizSuivi key={quiz.id} quiz={quiz} />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </details>
  );
}

function pastilleStatut(devoir: DevoirSuivi): { texte: string; ton: TonPastille } {
  if (devoir.statut === 'A_RENDRE') {
    return devoir.enRetard ? { texte: 'En retard', ton: 'danger' } : { texte: 'À rendre', ton: 'warning' };
  }
  const texte = LIBELLES_STATUT_DEVOIR[devoir.statut];
  if (devoir.enRetard) return { texte: `${texte} en retard`, ton: 'warning' };
  return { texte, ton: devoir.statut === 'CORRIGE' ? 'success' : 'info' };
}

function LigneDevoir({ devoir }: { devoir: DevoirSuivi }): JSX.Element {
  const statut = pastilleStatut(devoir);
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3 sm:px-5">
      <div className="min-w-0 flex-1 basis-56">
        <div className="text-sm font-semibold text-codialis-navy">
          {devoir.titre}
          {devoir.type === 'QUIZ' && <span className="ml-1 text-xs font-normal text-codialis-gray">· quiz</span>}
        </div>
        <div className="text-xs text-codialis-gray">{devoir.contexte}</div>
        <div className="text-[11px] text-codialis-gray">
          Échéance : {echeanceLongue(devoir.echeance)}
          {devoir.renduLe &&
            ` · rendu le ${new Date(devoir.renduLe).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' })}`}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Pastille texte={statut.texte} ton={statut.ton} />
        {devoir.note && (
          <span className="text-sm font-bold text-codialis-navy">
            {noteSur20(devoir.note.valeur)}
            {!devoir.note.publiee && <span className="ml-1 text-[11px] font-normal text-codialis-gray">provisoire</span>}
          </span>
        )}
      </div>
    </li>
  );
}

function Fiche({ fiche }: { fiche: FicheProgressionApprenant }): JSX.Element {
  const { apprenant, progression, assiduite, promotion } = fiche;
  const ouverts = progression.refus === null;
  const parBloc = new Map(progression.blocs.map((bloc) => [bloc.id, bloc]));
  const presence = assiduite.tauxPresence;

  return (
    <>
      <section className="min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-sm font-bold text-white">
              {`${apprenant.prenom.slice(0, 1)}${apprenant.nom.slice(0, 1)}`.toUpperCase()}
            </span>
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-bold text-codialis-navy">
                {apprenant.prenom} {apprenant.nom}
              </h1>
              <p className="text-sm text-codialis-gray">
                {fiche.formation.titre}
                {promotion.libelle !== fiche.formation.titre && ` · ${promotion.libelle}`}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <PastilleSite site={apprenant.siteCode} />
                <Pastille texte={apprenant.mode === 'OF' ? 'Formation continue' : 'Apprentissage'} ton="info" />
                {apprenant.suiviParVous && <Pastille texte="Suivi par vous" ton="info" />}
              </div>
              <p className="mt-1 text-xs text-codialis-gray">
                {[
                  `Référent : ${apprenant.referent ?? '—'}`,
                  apprenant.dateEntree ? `entrée le ${jourLisible(apprenant.dateEntree)}` : null,
                  apprenant.dateFinPrevue ? `fin prévue le ${jourLisible(apprenant.dateFinPrevue)}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          </div>
          {fiche.dossierOuvrable && (
            <Link
              to={`/learners/${encodeURIComponent(apprenant.id)}`}
              className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-codialis-navy hover:bg-gray-50"
            >
              Ouvrir le dossier
            </Link>
          )}
        </div>

        {apprenant.signaux.length > 0 ? (
          <div className="mt-4 rounded-xl border border-red-100 bg-red-50/60 px-4 py-3">
            <div className="text-sm font-semibold text-red-700">En retard</div>
            <ul className="mt-1 space-y-1 text-sm text-red-700">
              {apprenant.signaux.map((signal) => (
                <li key={signal}>{motifDuSignal(signal, fiche)}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="mt-4 rounded-xl bg-codialis-mint/10 px-4 py-3 text-sm text-codialis-navy">
            Aucun retard : ni devoir échu, ni absentéisme au-delà du seuil, ni progression loin sous la moyenne de sa
            promotion.
          </p>
        )}
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tuile libelle="Progression en ligne">
          <Valeur alerte={apprenant.signaux.includes('PROGRESSION')}>
            {ouverts ? tauxLisible(progression.formation) : '—'}
          </Valeur>
          <div className="mt-3">
            <BarreAvancement
              avancement={progression.formation}
              libelle="Progression dans les cours en ligne"
              hauteur="h-1"
            />
          </div>
          <div className="mt-1 text-xs text-codialis-gray">
            {!ouverts
              ? 'cours en ligne fermés'
              : progression.formation.taux === null
                ? 'rien à suivre en ligne'
                : `${progression.formation.faits}/${progression.formation.total} leçons et quiz · moyenne de la promotion ${
                    promotion.tauxMoyen === null ? '—' : `${promotion.tauxMoyen} %`
                  }`}
          </div>
        </Tuile>
        <Tuile libelle="Quiz">
          <Valeur>
            {progression.quiz.scoreMoyen === null ? '—' : nombre(progression.quiz.scoreMoyen)}
            {progression.quiz.scoreMoyen !== null && <span className="text-base text-codialis-gray">/20</span>}
          </Valeur>
          <div className="mt-3 text-xs text-codialis-gray">
            {!ouverts
              ? 'cours en ligne fermés'
              : progression.quiz.total === 0
                ? 'aucun quiz au programme'
                : `${progression.quiz.faits} fait${progression.quiz.faits > 1 ? 's' : ''} sur ${progression.quiz.total}${
                    progression.quiz.scoreMoyen === null ? '' : ' · score moyen'
                  }`}
          </div>
        </Tuile>
        <Tuile libelle="Devoirs">
          <Valeur alerte={apprenant.devoirs.enRetard > 0}>{apprenant.devoirs.enRetard}</Valeur>
          <div className="mt-3 text-xs text-codialis-gray">
            en retard · {apprenant.devoirs.aRendre} à rendre sur {pluriel(apprenant.devoirs.demandes, 'demandé')}
          </div>
        </Tuile>
        <Tuile libelle="Assiduité">
          <Valeur alerte={apprenant.absenteisme.auDelaDuSeuil}>{pourcentagePresence(presence)}</Valeur>
          <div className="mt-3 text-xs text-codialis-gray">
            présence · {nombre(assiduite.heuresSuivies)} h suivies
            {apprenant.absenteisme.demiJournees > 0 &&
              ` · absentéisme ${pourcentagePresence(apprenant.absenteisme.taux)} (seuil ${fiche.regles.seuilAbsenteisme} %)`}
          </div>
        </Tuile>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section
          className="min-w-0 overflow-hidden rounded-2xl bg-white shadow-card lg:col-span-2"
          aria-labelledby="programme"
        >
          <div className="border-b border-gray-100 px-4 py-4 sm:px-5">
            <h2 id="programme" className="font-display font-bold text-codialis-navy">
              Progression bloc par bloc
            </h2>
            <p className="text-xs text-codialis-gray">
              {progression.derniereActivite
                ? `Dernière activité en ligne ${momentRelatif(progression.derniereActivite)}`
                : 'Aucune activité en ligne pour l’instant'}
            </p>
          </div>
          {!ouverts ? (
            <p className="m-4 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">{progression.refus}</p>
          ) : (
            <>
              {fiche.formation.blocs.map((bloc, rang) => (
                <BlocSuivi key={bloc.id} numero={rang + 1} libelle={bloc.libelle} bloc={parBloc.get(bloc.id)} />
              ))}
              {progression.transversal.cours.length > 0 && (
                <div className="space-y-2 px-4 py-4 sm:px-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-codialis-gray">Cours transversaux</div>
                  <ul className="space-y-2">
                    {progression.transversal.cours.map((cours) => (
                      <CoursSuivi key={cours.id} cours={cours} />
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </section>

        <section className="min-w-0 overflow-hidden rounded-2xl bg-white shadow-card" aria-labelledby="devoirs">
          <div className="border-b border-gray-100 px-4 py-4 sm:px-5">
            <h2 id="devoirs" className="font-display font-bold text-codialis-navy">
              Devoirs et quiz
            </h2>
            <p className="text-xs text-codialis-gray">Par échéance · note publiée, ou automatique et provisoire</p>
          </div>
          {fiche.devoirs.length === 0 ? (
            <p className="px-4 py-6 text-sm text-codialis-gray sm:px-5">Aucun devoir demandé pour l’instant.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {fiche.devoirs.map((devoir) => (
                <LigneDevoir key={devoir.id} devoir={devoir} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

export function InstructorProgressionApprenantPage(): JSX.Element {
  const { id = '' } = useParams();
  const { data, isLoading, error } = useFicheProgression(id);
  const retour = data ? `/instructor-promos?promotion=${encodeURIComponent(data.promotion.cle)}` : '/instructor-promos';

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <Link
        to={retour}
        className="inline-flex min-h-[36px] items-center gap-1.5 text-sm font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {data?.vue === 'DIRECTION' ? 'Suivi des promotions' : 'Mes promotions'}
      </Link>
      {isLoading && <Chargement libelle="Chargement de la fiche…" />}
      {error && (
        <MessageErreur
          message={error instanceof ErreurApi ? error.message : 'La fiche de progression n’a pas pu être chargée.'}
        />
      )}
      {data && <Fiche fiche={data} />}
    </div>
  );
}
